import { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { isDrawnRect, normalizeRect, pointerToPercent } from "./pdfFieldGeometry";

const DEFAULT_PAGE_NUMBER = 1;
const CONTAINER_BORDER_RADIUS = 4;
const FIELD_LABEL_FONT_SIZE = 11;
const FIELD_LABEL_FONT_WEIGHT = 700;
const FIELD_LABEL_PADDING = 0.4;
const FIELD_LABEL_TEXT_COLOR = "#0f172a";
const SELECTED_BORDER_COLOR = "primary.main";
const SELECTED_BACKGROUND_COLOR = "rgba(25,118,210,0.18)";
const CLIENT_BORDER_COLOR = "warning.main";
const CLIENT_BACKGROUND_COLOR = "rgba(245,158,11,0.18)";
const OWNER_BORDER_COLOR = "success.main";
const OWNER_BACKGROUND_COLOR = "rgba(34,197,94,0.16)";
const DRAFT_BORDER_COLOR = "secondary.main";
const DRAFT_BACKGROUND_COLOR = "rgba(236,72,153,0.14)";
const PULSE_DURATION = "900ms";
const PULSE_REPEATS = 2;
// Ring drawn outside the box, so the pulse reads even on a field only a few pixels tall.
const PULSE_KEYFRAMES = {
  "0%": { boxShadow: "0 0 0 0 rgba(25,118,210,0.55)" },
  "70%": { boxShadow: "0 0 0 10px rgba(25,118,210,0)" },
  "100%": { boxShadow: "0 0 0 0 rgba(25,118,210,0)" },
};

function fieldBorderColor(field, isSelected) {
  if (isSelected) return SELECTED_BORDER_COLOR;
  return field.signerRole === "CLIENT" ? CLIENT_BORDER_COLOR : OWNER_BORDER_COLOR;
}

function fieldBackgroundColor(field, isSelected) {
  if (isSelected) return SELECTED_BACKGROUND_COLOR;
  return field.signerRole === "CLIENT" ? CLIENT_BACKGROUND_COLOR : OWNER_BACKGROUND_COLOR;
}

/**
 * The drag-to-draw PDF field-mapping overlay, shared by the admin Document Centre and the college
 * field-mapping page. It owns only the in-progress drag (origin + draft rectangle); the parent owns
 * the field list and decides what a finished rectangle means (e.g. which field it gets assigned to).
 *
 * Visual/interaction behaviour mirrors the overlay this was extracted from, with one correction:
 * a drag is only reported once it clears MIN_DRAW_PCT on both axes. The original reported every
 * mouseup, so clicking an existing field box to select it (whose mousedown reaches this layer)
 * wrote a 0x0 rectangle over whichever field was selected, silently unmapping it.
 */
const PdfFieldCanvas = ({
  pageImageUrl,
  fields = [],
  activePageNumber = DEFAULT_PAGE_NUMBER,
  selectedFieldIndex = null,
  onSelectField,
  onRectangleDrawn,
  readOnly = false,
}) => {
  const layerRef = useRef(null);
  const selectedBoxRef = useRef(null);
  const [origin, setOrigin] = useState(null);
  const [draft, setDraft] = useState(null);
  // Bumped on every selection so the highlight replays even when the same field is picked twice.
  // A CSS animation restarts only when the node remounts, which this nonce forces through its key.
  const [pulseNonce, setPulseNonce] = useState(0);

  // Which box a field row refers to is otherwise guesswork on a dense form: bring the selected
  // rectangle into view on whatever page it lives on, then flash it.
  useEffect(() => {
    setPulseNonce((nonce) => nonce + 1);
    const node = selectedBoxRef.current;
    if (!node || typeof node.scrollIntoView !== "function") return;
    node.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
  }, [selectedFieldIndex, activePageNumber]);

  // Paired with its position in the full list, because a field's name is deliberately NOT unique:
  // the backend accepts a repeated name as "mirror one value onto several spots". Keying or
  // selecting by name made every copy resolve to the first one, so clicking a mirrored box on
  // page 3 selected and then moved the page 1 field instead.
  const pageFields = fields
    .map((field, index) => ({ field, index }))
    .filter((entry) => entry.field.pageNumber === activePageNumber);

  const startDraw = (event) => {
    if (readOnly || !layerRef.current) return;
    const point = pointerToPercent(event, layerRef.current);
    setOrigin(point);
    setDraft({ xPct: point.x, yPct: point.y, widthPct: 0, heightPct: 0 });
  };

  const moveDraw = (event) => {
    if (readOnly || !origin || !layerRef.current) return;
    const point = pointerToPercent(event, layerRef.current);
    setDraft(normalizeRect(origin.x, origin.y, point.x, point.y));
  };

  const endDraw = () => {
    if (readOnly) return;
    if (isDrawnRect(draft) && onRectangleDrawn) {
      onRectangleDrawn({ ...draft, pageNumber: activePageNumber });
    }
    setOrigin(null);
    setDraft(null);
  };

  const handleSelectField = (event, index) => {
    event.stopPropagation();
    if (onSelectField) onSelectField(index);
  };

  return (
    <Box
      ref={layerRef}
      sx={{
        position: "relative",
        borderRadius: CONTAINER_BORDER_RADIUS,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        cursor: readOnly ? "default" : "crosshair",
      }}
      onMouseDown={startDraw}
      onMouseMove={moveDraw}
      onMouseUp={endDraw}
      onMouseLeave={() => origin && endDraw()}
    >
      {pageImageUrl && (
        <img
          src={pageImageUrl}
          alt={`Page ${activePageNumber}`}
          style={{ width: "100%", display: "block" }}
        />
      )}

      {pageFields.map(({ field, index }) => {
        const isSelected = index === selectedFieldIndex;
        return (
          <Box
            key={isSelected ? `${index}-selected-${pulseNonce}` : index}
            ref={isSelected ? selectedBoxRef : undefined}
            onClick={(event) => handleSelectField(event, index)}
            sx={{
              position: "absolute",
              left: `${field.xPct * 100}%`,
              top: `${field.yPct * 100}%`,
              width: `${field.widthPct * 100}%`,
              height: `${field.heightPct * 100}%`,
              border: "2px solid",
              borderColor: fieldBorderColor(field, isSelected),
              backgroundColor: fieldBackgroundColor(field, isSelected),
              color: FIELD_LABEL_TEXT_COLOR,
              fontSize: FIELD_LABEL_FONT_SIZE,
              fontWeight: FIELD_LABEL_FONT_WEIGHT,
              p: FIELD_LABEL_PADDING,
              display: "flex",
              alignItems: "center",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              ...(isSelected && {
                zIndex: 1,
                "@keyframes pdfFieldPulse": PULSE_KEYFRAMES,
                animation: `pdfFieldPulse ${PULSE_DURATION} ease-out ${PULSE_REPEATS}`,
              }),
            }}
          >
            {field.label}
          </Box>
        );
      })}

      {draft && (
        <Box
          sx={{
            position: "absolute",
            left: `${draft.xPct * 100}%`,
            top: `${draft.yPct * 100}%`,
            width: `${draft.widthPct * 100}%`,
            height: `${draft.heightPct * 100}%`,
            border: "2px dashed",
            borderColor: DRAFT_BORDER_COLOR,
            backgroundColor: DRAFT_BACKGROUND_COLOR,
          }}
        />
      )}
    </Box>
  );
};

export default PdfFieldCanvas;
